import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

/**
 * Custom hook to handle session timeout after 30 minutes of inactivity
 * @param {number} timeout - Timeout duration in milliseconds (default: 30 minutes)
 */
const useSessionTimeout = (timeout = 30 * 60 * 1000) => {
  const navigate = useNavigate();
  const timeoutIdRef = useRef(null);
  const isAuthenticatedRef = useRef(false);

  // Logout function
  const logout = useCallback(async () => {
    // Sign out of Supabase so the JWT is invalidated server-side
    try {
      await supabase?.auth?.signOut();
    } catch (err) {
      console.error('Supabase signOut error during session timeout:', err);
    }

    // Clear local authentication data
    localStorage.removeItem('userRole');
    localStorage.removeItem('sessionTimestamp');
    
    // Clear the timeout
    if (timeoutIdRef?.current) {
      clearTimeout(timeoutIdRef?.current);
      timeoutIdRef.current = null;
    }
    
    // Redirect to login
    navigate('/login', { replace: true });
  }, [navigate]);

  // Reset the timeout timer
  const resetTimer = useCallback(() => {
    // Only reset timer if user is authenticated
    const userRole = localStorage.getItem('userRole');
    if (!userRole) return;

    // Clear existing timeout
    if (timeoutIdRef?.current) {
      clearTimeout(timeoutIdRef?.current);
    }

    // Update session timestamp
    localStorage.setItem('sessionTimestamp', Date.now()?.toString());

    // Set new timeout
    timeoutIdRef.current = setTimeout(() => {
      logout();
    }, timeout);
  }, [logout, timeout]);

  // Activity event handlers
  const handleActivity = useCallback(() => {
    const userRole = localStorage.getItem('userRole');
    if (userRole) {
      resetTimer();
    }
  }, [resetTimer]);

  useEffect(() => {
    // Check if user is authenticated
    const userRole = localStorage.getItem('userRole');
    isAuthenticatedRef.current = !!userRole;

    if (!userRole) {
      // If not authenticated, don't set up timeout
      return;
    }

    // Check if session has expired on mount
    const sessionTimestamp = localStorage.getItem('sessionTimestamp');
    if (sessionTimestamp) {
      const elapsed = Date.now() - parseInt(sessionTimestamp, 10);
      if (elapsed >= timeout) {
        // Session expired
        logout();
        return;
      }
    }

    // Initialize the timer
    resetTimer();

    // Activity events to monitor
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Add event listeners
    events?.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Cleanup function
    return () => {
      // Remove event listeners
      events?.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });

      // Clear timeout
      if (timeoutIdRef?.current) {
        clearTimeout(timeoutIdRef?.current);
      }
    };
  }, [timeout, resetTimer, handleActivity, logout]);

  return { logout, resetTimer };
};

export default useSessionTimeout;